import { useEffect, useState, useCallback } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface PwaInstallState {
  isInstallable: boolean;
  isInstalled: boolean;
  isIos: boolean;
  showIosInstructions: boolean;
  install: () => Promise<{ promptShown: boolean; outcome?: 'accepted' | 'dismissed' }>;
}

export function usePwaInstall(): PwaInstallState {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIos, setIsIos] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const ua = window.navigator.userAgent.toLowerCase();
    const ios =
      /iphone|ipad|ipod/.test(ua) &&
      /safari/.test(ua) &&
      !/crios|fxios/.test(ua);
    setIsIos(ios);

    const standaloneIos =
      'standalone' in window.navigator &&
      (window.navigator as any).standalone;

    const standaloneDisplayMode =
      window.matchMedia &&
      window.matchMedia('(display-mode: standalone)').matches;

    if (standaloneIos || standaloneDisplayMode) {
      setIsInstalled(true);
    }

    const handleBeforeInstallPrompt = (e: BeforeInstallPromptEvent) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt as EventListener);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt as EventListener);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const install = useCallback(async () => {
    if (!deferredPrompt) {
      return { promptShown: false as const };
    }

    deferredPrompt.prompt();
    const choiceResult = await deferredPrompt.userChoice;

    setDeferredPrompt(null);

    return {
      promptShown: true as const,
      outcome: choiceResult.outcome,
    };
  }, [deferredPrompt]);

  return {
    isInstallable: isInstallable && !isInstalled,
    isInstalled,
    isIos,
    showIosInstructions: isIos && !isInstalled,
    install,
  };
}
